import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const eur = (c) => `€${c.toLocaleString("en-US")}`;
const COUNTRIES = ["Netherlands", "Belgium", "Germany", "France", "United Kingdom", "Luxembourg", "Austria", "Spain", "Italy", "Ireland", "Other"];

export default function Preorder() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", address: "", postal_code: "", city: "", country: "Netherlands",
  });

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => setSettings({ shipping_cost: 0, preorder_payment_mode: "payment" }));
  }, []);

  useEffect(() => {
    if (user && user.email) setForm((f) => ({ ...f, email: f.email || user.email, full_name: f.full_name || user.name || "" }));
  }, [user]);

  useEffect(() => {
    if (items.length === 0) navigate("/collection");
  }, [items.length, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const shipping = (settings?.shipping_cost || 0) * 100;
  const total = subtotal * 100 + shipping;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/preorders", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        customer: form,
        origin_url: window.location.origin,
      });
      if (data.mode === "payment" && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      clearCart();
      navigate(`/preorder/success?ref=${data.reference}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="pt-32 md:pt-40 pb-24" data-testid="preorder-page">
      <div className="container-lux max-w-5xl">
        <Reveal>
          <p className="label-mono mb-6 text-blood">Pre-Order</p>
          <h1 className="heading-serif text-5xl md:text-6xl mb-5 leading-[0.95]">Reserve your obsession</h1>
          <div className="inline-block border border-blood/40 text-blood label-mono px-4 py-2 mb-10" data-testid="preorder-soldout-note">
            Out of stock — available for pre-order
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* form */}
          <form onSubmit={submit} className="lg:col-span-3 space-y-7" data-testid="preorder-form">
            <Field label="Full name"><input required value={form.full_name} onChange={set("full_name")} data-testid="pre-name" className={inp} /></Field>
            <div className="grid sm:grid-cols-2 gap-7">
              <Field label="Email"><input required type="email" value={form.email} onChange={set("email")} data-testid="pre-email" className={inp} /></Field>
              <Field label="Phone (optional)"><input value={form.phone} onChange={set("phone")} data-testid="pre-phone" className={inp} /></Field>
            </div>
            <Field label="Shipping address"><input required value={form.address} onChange={set("address")} data-testid="pre-address" className={inp} /></Field>
            <div className="grid sm:grid-cols-3 gap-7">
              <Field label="Postal code"><input required value={form.postal_code} onChange={set("postal_code")} data-testid="pre-postal" className={inp} /></Field>
              <Field label="City"><input required value={form.city} onChange={set("city")} data-testid="pre-city" className={inp} /></Field>
              <Field label="Country">
                <select value={form.country} onChange={set("country")} data-testid="pre-country" className={inp + " bg-ink"}>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <button type="submit" disabled={loading} data-testid="preorder-submit"
              className="btn-relic w-full border-blood bg-blood text-ivory hover:bg-ivory hover:border-ivory hover:text-ink disabled:opacity-50">
              {loading ? "Placing pre-order…" : (settings?.preorder_payment_mode === "payment" ? "Pre-Order & Pay" : "Place Pre-Order")}
            </button>
            <p className="label-mono text-smoke leading-relaxed">
              {settings?.preorder_payment_mode === "payment"
                ? "You will be taken to secure checkout to reserve with payment. We only confirm once your payment provider verifies it."
                : "No payment is taken now. We will email you payment instructions before your fragrance ships."}
            </p>
          </form>

          {/* summary */}
          <aside className="lg:col-span-2 bg-onyx border border-smoke/15 p-7 h-fit" data-testid="preorder-summary">
            <p className="label-mono mb-6">Order Summary</p>
            <div className="space-y-4 border-b border-smoke/15 pb-5">
              {items.map((i) => (
                <div key={i.slug} className="flex justify-between gap-4" data-testid={`summary-item-${i.slug}`}>
                  <span className="font-sans text-sm text-ivory/85">{i.name} <span className="text-smoke">×{i.quantity}</span></span>
                  <span className="font-mono text-sm">{eur(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between py-3 text-sm font-sans text-ivory/75">
              <span>Subtotal</span><span className="font-mono">{eur(subtotal)}</span>
            </div>
            <div className="flex justify-between pb-4 text-sm font-sans text-ivory/75 border-b border-smoke/15">
              <span>Shipping</span><span className="font-mono">{shipping > 0 ? eur(shipping / 100) : "Calculated later"}</span>
            </div>
            <div className="flex justify-between pt-5 items-baseline">
              <span className="label-mono">Total</span>
              <span className="heading-serif text-2xl" data-testid="preorder-total">{eur(total / 100)}</span>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full bg-transparent border-b border-smoke/40 py-3 focus:outline-none focus:border-blood transition-colors font-sans text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="label-mono block mb-2">{label}</label>
      {children}
    </div>
  );
}
